import React, { useEffect, useMemo, useState } from 'react';
import { parseApiUtc } from '@/utils/apiDate';
import { useTranslation } from 'react-i18next';
import { appDateTime } from '@/utils/appDateFormat';
import { Search } from 'lucide-react';
import {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    TableCell,
} from '../../../../components/ui/Table';
import Pagination from '../../../../components/Pagination';
import VersionViewerModal from '../../../../components/TermsAndPolicies/VersionViewerModal';
import { getAcceptanceList } from '../../../../api/General/termsAndPolicies';
import type {
    AcceptanceListView,
    AcceptanceRowView,
    AcceptanceStatusFilter,
} from '../../../../models/general';

const STATUS_OPTIONS: AcceptanceStatusFilter[] = ['all', 'accepted', 'notAccepted', 'stale'];

function statusLabel(status: AcceptanceStatusFilter, t: (k: string) => string): string {
    switch (status) {
        case 'accepted': return t('Accepted');
        case 'notAccepted': return t('Not accepted');
        case 'stale': return t('Stale');
        default: return t('All');
    }
}

function StatusBadge({ row, t }: { row: AcceptanceRowView; t: (k: string) => string }) {
    if (!row.hasAccepted) {
        return (
            <span
                data-testid={`acceptance-status-${row.userId}`}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200"
            >
                {t('Not accepted')}
            </span>
        );
    }
    if (row.isStale) {
        return (
            <span
                data-testid={`acceptance-status-${row.userId}`}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
            >
                <span data-testid={`stale-badge-${row.userId}`}>{t('Stale')}</span>
            </span>
        );
    }
    return (
        <span
            data-testid={`acceptance-status-${row.userId}`}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200"
        >
            {t('Accepted')}
        </span>
    );
}

export default function AcceptanceTab() {
    const { t } = useTranslation();
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [status, setStatus] = useState<AcceptanceStatusFilter>('all');
    const [data, setData] = useState<AcceptanceListView | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [versionOpen, setVersionOpen] = useState(false);
    const [versionToView, setVersionToView] = useState<number | null>(null);

    // Debounce search input → search query, resetting to page 1.
    useEffect(() => {
        const handle = setTimeout(() => {
            setSearch(searchInput.trim());
            setPage(1);
        }, 300);
        return () => clearTimeout(handle);
    }, [searchInput]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);
        getAcceptanceList({ page, pageSize, search: search || undefined, status })
            .then(res => {
                if (cancelled) return;
                if (res.errors?.length || !res.payload) {
                    setError(res.errors?.[0]?.description ?? t('Failed to load acceptance'));
                    setData(null);
                } else {
                    setData(res.payload);
                }
            })
            .catch(() => { if (!cancelled) setError(t('Failed to load acceptance')); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [page, pageSize, search, status, t]);

    const summaryText = useMemo(() => {
        if (!data) return '';
        if (!data.hasPublishedTerms || data.currentVersion == null) {
            return t('No version has been published yet.');
        }
        return t('{{accepted}} / {{total}} employees on v{{version}}', {
            accepted: data.acceptedOnCurrent,
            total: data.totalEmployees,
            version: data.currentVersion,
        });
    }, [data, t]);

    const openVersion = (v: number) => {
        setVersionToView(v);
        setVersionOpen(true);
    };

    return (
        <div className="flex flex-col gap-4">
            {/* Summary */}
            <div
                className="rounded-md border border-border bg-white p-4"
                data-testid="acceptance-summary"
            >
                <div className="text-sm font-medium text-muted-foreground">
                    {t('Acceptance overview')}
                </div>
                <div className="mt-1 text-lg font-semibold">
                    {data ? summaryText : t('Loading…')}
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                    <Search className="absolute top-1/2 -translate-y-1/2 ltr:left-3 rtl:right-3 w-4 h-4 text-muted-foreground" />
                    <input
                        data-testid="acceptance-search-input"
                        type="text"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder={t('Search employees')}
                        className="w-full ltr:pl-9 rtl:pr-9 pe-3 py-2 text-sm rounded-md border border-border bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                    />
                </div>
                <select
                    data-testid="acceptance-status-filter"
                    value={status}
                    onChange={(e) => { setStatus(e.target.value as AcceptanceStatusFilter); setPage(1); }}
                    className="px-3 py-2 text-sm rounded-md border border-border bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                >
                    {STATUS_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{statusLabel(opt, t)}</option>
                    ))}
                </select>
            </div>

            {error && (
                <div className="p-3 rounded-md text-sm bg-rose-50 text-rose-700 border border-rose-200">
                    {error}
                </div>
            )}

            {/* Table */}
            <div className="rounded-md border border-border bg-white overflow-hidden">
                <Table data-testid="acceptance-table">
                    <TableHeader>
                        <TableRow>
                            <TableHead>{t('Name')}</TableHead>
                            <TableHead>{t('Email')}</TableHead>
                            <TableHead>{t('Accepted Version')}</TableHead>
                            <TableHead>{t('Accepted At')}</TableHead>
                            <TableHead>{t('Status')}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                                    {t('Loading…')}
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && data && data.page.items.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={5} className="text-center text-muted-foreground py-6" data-testid="acceptance-empty">
                                    {t('No employees match these filters')}
                                </TableCell>
                            </TableRow>
                        )}
                        {!loading && data?.page.items.map(row => (
                            <TableRow key={row.userId} data-testid={`acceptance-row-${row.userId}`}>
                                <TableCell className="font-medium">{row.fullName ?? '—'}</TableCell>
                                <TableCell className="text-muted-foreground">{row.email ?? '—'}</TableCell>
                                <TableCell>
                                    {row.acceptedVersion != null ? (
                                        <button
                                            type="button"
                                            onClick={() => openVersion(row.acceptedVersion!)}
                                            data-testid={`acceptance-version-${row.userId}`}
                                            className="text-primary hover:underline"
                                        >
                                            v{row.acceptedVersion}
                                        </button>
                                    ) : '—'}
                                </TableCell>
                                <TableCell className="text-muted-foreground">
                                    {row.acceptedAt ? (appDateTime(parseApiUtc(row.acceptedAt)) || '—') : '—'}
                                </TableCell>
                                <TableCell>
                                    <StatusBadge row={row} t={t} />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>

            {data && data.page.totalPages > 0 && (
                <div data-testid="acceptance-pagination">
                    <Pagination
                        currentPage={data.page.pageNumber}
                        totalPages={data.page.totalPages}
                        onPageChange={setPage}
                        hasPreviousPage={data.page.hasPreviousPage}
                        hasNextPage={data.page.hasNextPage}
                        pageSize={pageSize}
                        pageSizeOptions={[10, 25, 50, 100]}
                        onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
                        totalCount={data.page.totalCount}
                    />
                </div>
            )}

            <VersionViewerModal
                version={versionToView}
                open={versionOpen}
                onOpenChange={setVersionOpen}
            />
        </div>
    );
}
